import React, { useEffect, useMemo, useState } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $createParagraphNode,
  $getSelection,
  $isParagraphNode,
  $isRangeSelection,
} from 'lexical';
import { getSelectedNode } from '../../utils';
import { Dropdown } from 'semantic-ui-react';
import {
  $isHeadingNode,
  $isQuoteNode,
  $createQuoteNode,
  $createHeadingNode,
} from '@lexical/rich-text';
import { $isCodeNode, $createCodeNode } from '@lexical/code';
import { $setBlocksType_experimental } from '@lexical/selection';

import paragraph from 'src/assets/editor_images/paragraph.svg';
import h1 from 'src/assets/editor_images/type-h1.svg';
import h2 from 'src/assets/editor_images/type-h2.svg';
import quote from 'src/assets/editor_images/chat-square-quote.svg';
import code from 'src/assets/editor_images/code.svg';
import { useSetting } from 'src/components/custom/Editor/hooks';

const blockTypeToBlockName = {
  paragraph: {
    text: 'Normal',
    icon: paragraph,
  },
  h1: {
    text: 'Large Heading',
    icon: h1,
  },
  h2: {
    text: 'Small Heading',
    icon: h2,
  },
  quote: {
    text: 'Quote',
    icon: quote,
  },
  code: {
    text: 'Code Block',
    icon: code,
  },
};

export type TextBlockProps = {
  blockType?: keyof typeof blockTypeToBlockName;
};

const FormatTextBlock: React.FC<TextBlockProps> = () => {
  const [blockType, setBlockType] =
    useState<keyof typeof blockTypeToBlockName>('paragraph');
  const options = useMemo(() => {
    return Object.entries(blockTypeToBlockName).map(([key, block]) => {
      return {
        key,
        value: key,
        text: block.text,
        image: block.icon,
      };
    });
  }, []);
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return;

        const node = getSelectedNode(selection);
        const nodeParent = node.getParent();

        if (!nodeParent) return;

        if ($isParagraphNode(nodeParent)) {
          setBlockType('paragraph');
        }
        if ($isHeadingNode(nodeParent)) {
          setBlockType(nodeParent.getTag() as 'h1' | 'h2');
        }
        if ($isQuoteNode(nodeParent)) {
          setBlockType('quote');
        }
        if ($isCodeNode(nodeParent)) {
          setBlockType('code');
        }
      });
    });
  }, [editor]);

  const { editable } = useSetting();

  return (
    <React.Fragment>
      <Dropdown
        disabled={!editable}
        closeOnChange={false}
        value={blockType}
        icon={null}
        trigger={
          <button
            className="toolbar-item block-controls"
            aria-label="Formatting Options"
            disabled={!editable}
          >
            <span className={'icon block-type ' + blockType} />
            <span className="text">
              {blockTypeToBlockName[blockType]?.text}
            </span>
            <i className="chevron-down" />
          </button>
        }
        options={options}
        onChange={(event, data) => {
          const type = data.value as keyof typeof blockTypeToBlockName;
          setBlockType(type);

          editor.update(() => {
            const selection = $getSelection();

            if ($isRangeSelection(selection)) {
              switch (type) {
                case 'h1':
                  return $setBlocksType_experimental(selection, () =>
                    $createHeadingNode('h1'),
                  );
                case 'h2':
                  return $setBlocksType_experimental(selection, () =>
                    $createHeadingNode('h2'),
                  );
                case 'code':
                  return $setBlocksType_experimental(selection, () =>
                    $createCodeNode(),
                  );
                case 'quote':
                  return $setBlocksType_experimental(selection, () =>
                    $createQuoteNode(),
                  );
                default:
                  return $setBlocksType_experimental(selection, () =>
                    $createParagraphNode(),
                  );
              }
            }
          });
        }}
      />
    </React.Fragment>
  );
};

export default FormatTextBlock;
